<template>
  <!-- Create Organization Modal -->
  <div v-if="showCreateModal" class="flex items-center justify-center inset-0 fixed bg-black/50 backdrop-blur-sm z-40">
    <div class="p-6 glass-card mb-2 w-[80%] max-w-md min-h-[35%] shadow-xl z-50 flex flex-col justify-between">
      <h2 class="text-xl font-bold mb-4">Create Organization</h2>
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium mb-2">Organization Name</label>
          <input v-model="newOrgName" type="text" placeholder="Acme Corp"
            class="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label class="block text-sm font-medium mb-2">Slug </label>
          <input v-model="newOrgSlug" placeholder="ac" rows="3"
            class="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p class="text-xs text-gray-200">Lowercase letters, numbers, and hyphens only</p>
        </div>
      </div>
      <div class="flex gap-3 mt-6">
        <button @click="handleCreateOrganization" :disabled="!newOrgName.trim() && !newOrgSlug.trim()"
          :class="['flex-1 px-4 py-2 rounded-md transition-all', newOrgName.trim() ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 text-gray-400 cursor-not-allowed']">
          Create
        </button>
        <button @click="closeCreateModal" class="flex-1 px-4 py-2 bg-gray-600 rounded-md hover:bg-gray-700 transition-all">Cancel</button>
      </div>
    </div>
  </div>

  <!-- Manage Organization Modal -->
  <div v-if="showManageModal" class="flex items-center justify-center inset-0 fixed bg-black/50 backdrop-blur-sm z-40">
    <div class="glass-card w-[90%] max-w-lg shadow-xl z-50 flex flex-col overflow-hidden rounded-xl">

      <!-- Header -->
      <div class="flex items-center justify-between px-6 pt-6 pb-4">
        <div class="flex items-center gap-3">
          <h2 class="text-xl font-bold">{{ managingOrg?.name }}</h2>
        </div>
        <button @click="closeManageModal" class="text-gray-400 transition-colors p-1 rounded-md hover:bg-transparent bg-transparent hover:text-red-500">
          <CircleX/>
        </button>
      </div>

      <!-- Tabs -->
      <div class="flex gap-2 px-6 pb-4">
        <button @click="manageTab = 'members'"
          :class="['flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all', manageTab === 'members' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700']">
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
          Members
        </button>
        <button @click="manageTab = 'settings'"
          :class="['flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all', manageTab === 'settings' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700']">
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"></path>
          </svg>
          Settings
        </button>
        <button @click="manageTab = 'devices'"
          :class="['flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all', manageTab === 'devices' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700']">
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"></path>
          </svg>
          Devices
        </button>
      </div>

      <!-- Tab: Members -->
      <div v-if="manageTab === 'members'" class="px-6 pb-6">
        <div class="flex items-center justify-between mb-4">
          <span class="text-sm font-semibold">Team Members</span>
          <button @click="showInviteRow = !showInviteRow"
            class="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="8.5" cy="7" r="4"></circle>
              <line x1="20" y1="8" x2="20" y2="14"></line>
              <line x1="23" y1="11" x2="17" y2="11"></line>
            </svg>
            Add Member
          </button>
        </div>

        <!-- Invite Row -->
        <div v-if="showInviteRow" class="flex gap-2 mb-4">
          <GlassSelect
            v-model="inviteEmail"
            :options="allUsers"
            placeholder="Select a user"
          />
          <GlassSelect
            v-model="inviteRole"
            :options="[{id:'member',name:'Member'},{id:'admin',name:'Admin'},{id:'viewer',name:'Viewer'}]"
            placeholder="Select a role"
          />
          <button @click="handleInviteMember" :disabled="!inviteEmail.trim()"
            :class="['px-4 py-2 rounded-md text-sm transition-all', inviteEmail.trim() ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 text-gray-400 cursor-not-allowed']">
            Invite
          </button>
        </div>

        <!-- Members List -->
        <div v-if="membersLoading" class="flex justify-center py-6">
          <svg class="animate-spin text-blue-500" xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
          </svg>
        </div>

        <div v-else class="space-y-2 max-h-64 overflow-y-auto pr-1">
          <div v-for="m in orgMembers" :key="m.id"
            class="flex items-center justify-between p-3 glass-card">
            <div class="flex items-center gap-3">
              <div class="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-sm text-white font-bold uppercase shrink-0">
                {{ (m['user'].username)[0] || '?' }}
              </div>
              <div>
                <p class="text-sm font-medium">{{ m['user'].email }}</p>
                <p v-if="m.name" class="text-xs text-gray-400">{{ m.email }}</p>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <span :class="['px-2 py-0.5 rounded-full text-xs font-medium uppercase', roleColor(m.role)]">{{ m.role }}</span>
              <button @click="handleRemoveMember(m)" class="text-gray-500 hover:text-red-400 transition-colors p-1 rounded-md hover:bg-gray-700" title="Remove">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            </div>
          </div>

          <div v-if="orgMembers.length === 0" class="text-center py-6 text-gray-500 text-sm">
            No members found.
          </div>
        </div>
      </div>


      <!-- Tab: Devices -->
      <div v-if="manageTab === 'devices'" class="px-6 pb-6">
        <div class="flex items-center justify-between mb-4">
          <span class="text-sm font-semibold">Devices</span>
          <button @click="showAssignRow= !showAssignRow"
            class="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="8.5" cy="7" r="4"></circle>
              <line x1="20" y1="8" x2="20" y2="14"></line>
              <line x1="23" y1="11" x2="17" y2="11"></line>
            </svg>
            Assign Devices
          </button>
        </div>

        <!-- Invite Row -->
        <div v-if="showAssignRow" class="flex gap-2 mb-4">

          <GlassSelect
            v-model="pendingDevice"
            :options="allDevices"
            placeholder="Select a device"
            :valueKey="'id'"
            :labelKey="'location'"
          />
          <button 
            @click="handleAssignDevice" 
            :class="['px-4 py-2 rounded-md text-sm transition-all bg-blue-600 hover:bg-blue-700']">
            Assign Device
          </button>
        </div>


        <div class="space-y-2 max-h-64 overflow-y-auto pr-1">
          <div v-for="d in assignedDevices" :key="d.id"
            class="flex items-center justify-between p-3 glass-card">
            <div class="flex items-center gap-3">
              <div class="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-sm text-white font-bold uppercase shrink-0">
                {{ d.name[0] }}
              </div>
              <div>
                <p class="text-sm font-medium">{{ d.name }}</p>
                <p v-if="d.name" class="text-xs text-gray-400">{{ d.mac_address }} | {{ d.location }}</p>
              </div>
            </div>
          </div>

          <div v-if="assignedDevices.length === 0" class="text-center py-6 text-gray-500 text-sm">
            No devices found.
          </div>
        </div>
      </div>

      <!-- Tab: Settings -->
      <div v-if="manageTab === 'settings'" class="px-6 pb-6 space-y-4">
        <div>
          <label class="block text-sm font-medium mb-2">Organization Name</label>
          <input v-model="editOrgName" type="text"
            class="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label class="block text-sm font-medium mb-2">Slug</label>
          <input v-model="editOrgSlug" type="text"
            class="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div class="flex space-x-2">
          <button 
          class="flex items-center gap-2 btn text-sm bg-red-600 hover:bg-red-700 transition-all"
          @click="deleteOrg"
          >
            <Trash2Icon/>
            Delete
          </button>
          <button @click="handleUpdateOrg" :disabled="!editOrgName.trim()"
            :class="['flex items-center gap-2 btn text-sm transition-all', editOrgName.trim() ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 text-gray-400 cursor-not-allowed']">
            Save Changes
          </button>
        </div>
      </div>

    </div>
  </div>

  <!-- Main Content -->
  <div class="p-6 dark:text-white glass-card-lite">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
      <div class="flex items-center gap-3">
        <div class="bg-blue-500/20 p-2 rounded-lg">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-blue-500">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
        </div>
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Organisations</h1>
          <p class="text-gray-400 text-sm mt-0.5">Manage your organisations and team members</p>
        </div>
      </div>
      <button @click="showCreateModal = true"
        class="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-all">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        <span>Create Organisation</span>
      </button>
    </div>

    <!-- Body Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Left: Your Organizations -->
      <div class="lg:col-span-2 glass-card-lite p-6">
        <div class="flex items-center gap-2 mb-5">
          <div class="w-1 h-5 bg-blue-500 rounded-full"></div>
          <h2 class="text-lg font-semibold">Organisations</h2>
        </div>

        <div v-if="loading" class="flex justify-center py-10">
          <svg class="animate-spin text-blue-500" xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
          </svg>
        </div>

        <div v-else-if="organizations.length === 0" class="text-center py-10 text-gray-500">
          No organisations found. Create one to get started.
        </div>

        <div v-else class="space-y-3">
          <div v-for="org in organizations" :key="org.id"
            :class="['p-4 glass-card transition-all']">
            <div class="flex items-start justify-between gap-3">
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="font-semibold">{{ org.name }}</span>
                  <span :class="['px-2 py-0.5 rounded-full text-xs font-medium uppercase', roleColor(org.role)]">{{ org.role }}</span>
                  <span v-if="activeOrgId === org.id" class="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">Active</span>
                </div>
                <p v-if="org.description" class="text-gray-400 text-sm mt-1 truncate">{{ org.description }}</p>
                <div class="flex items-center gap-1 mt-2 text-xs">
                  <Calendar class="h-3"/>
                  Created {{ formatDate(org.created_at) }}
                </div>
                <div class="flex items-center gap-1 mt-2  text-xs">
                  <Router class="h-3"/>
                  Devices: {{ org?.device_count || 0 }}
                </div>
                <div class="flex items-center gap-1 mt-2  text-xs">
                  <Users class="h-3"/>
                  Members: {{ org?.member_count || 0 }}
                </div>
              </div>

              <div class="flex items-center gap-2 shrink-0">
                <button v-if="activeOrgId !== org.id" @click="handleSwitchOrg(org)"
                  class="flex items-center gap-1 bg-gray-600 hover:bg-gray-700 hover:text-blue-300 text-xs transition-colors">
                  <UserPlus class="h-4"/>
                  Switch to this org
                </button>
                <button v-if="['owner', 'admin'].includes(org.role?.toLowerCase())" @click="manageOrg(org)"
                  class="flex items-center gap-1 btn bg-blue-600 hover:bg-blue-700 rounded-md text-xs transition-all">
                  <Blinds class="h-4"/>
                  Manage
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: Overview + Role Permissions -->
      <div class="flex flex-col gap-6">
        <div class="glass-card-lite p-6">
          <div class="flex items-center gap-2 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-blue-400">
              <line x1="18" y1="20" x2="18" y2="10"></line>
              <line x1="12" y1="20" x2="12" y2="4"></line>
              <line x1="6" y1="20" x2="6" y2="14"></line>
            </svg>
            <h2 class="text-lg font-semibold">Overview</h2>
          </div>
          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <span class="text-sm">Total Organisations</span>
              <span class="font-bold text-lg">{{ organizations.length }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm">Owned</span>
              <span class="font-bold text-lg">{{ ownedCount }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm">Member Of</span>
              <span class="font-bold text-lg">{{ memberCount }}</span>
            </div>
          </div>
        </div>

        <div class="glass-card-lite p-6">
          <div class="flex items-center gap-2 mb-4">
            <UserCheck2 class="h-4"/>
            <h2 class="text-lg font-semibold">Role Permissions</h2>
          </div>
          <div class="space-y-2">
            <div v-for="role in roles" :key="role.name" class="flex items-start gap-3 p-3 rounded-lg">
              <span :class="['px-2 py-0.5 rounded-full text-xs font-medium uppercase shrink-0 mt-0.5', roleColor(role.name)]">{{ role.name }}</span>
              <span class="text-xs leading-relaxed">{{ role.description }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Toast -->
    <div v-if="showSuccessToast"
      class="fixed bottom-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="20 6 9 17 4 12"></polyline>
      </svg>
      <span>{{ successMessage }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import {
  getOrganizations,
  createOrganization,
  updateOrganization,
  switchOrganization,
  getOrganizationMembers,
  inviteMember,
  removeMember,
  assignDevicetoOrg,
  deleteOrganization
} from '@/services/mivulite/organizationService';
import { getUsers } from '@/services/mivulite/usersService';
import { getAllDevicesbyOrg, getAllDevices } from '@/services/mivulite/devicesService';
import { Trash, Trash2Icon, Calendar, Users, Router, Blinds, UserPlus, UserCheck2, CircleX } from 'lucide-vue-next';
import GlassSelect from '@/components/GlassSelect.vue';

const organizations = ref([]);
const activeOrgId = ref(null);
const loading = ref(false);

// Create modal
const showCreateModal = ref(false);
const newOrgName = ref('');
const newOrgSlug = ref('');

// Manage modal
const showManageModal = ref(false);
const managingOrg = ref(null);
const manageTab = ref('members');
const orgMembers = ref([]);
const membersLoading = ref(false);

// Invite
const showInviteRow = ref(false);
const inviteEmail = ref('');
const inviteRole = ref('member');


//Assign Device
const showAssignRow = ref(false);
const pendingDevice = ref('');


// Settings edit
const editOrgName = ref('');
const editOrgSlug = ref('');


// Devices
const assignedDevices = ref([]);
const allDevices = ref([]);

// Toast
const showSuccessToast = ref(false);
const successMessage = ref('');

const allUsers = ref([]);

const roles = [
  { name: 'owner', description: 'Full control including billing, deletion, and ownership transfer.' },
  { name: 'admin', description: 'Manage members, settings, and resources.' },
  { name: 'member', description: 'Access and contribute to organisation resources.' },
  { name: 'viewer', description: 'Read-only access to organisation resources.' },
];

const ownedCount = computed(() => organizations.value.filter(o => o.role?.toLowerCase() === 'owner').length);
const memberCount = computed(() => organizations.value.filter(o => o.role?.toLowerCase() !== 'owner').length);

const roleColor = (role) => {
  const map = {
    owner: 'bg-red-500/20 text-red-400',
    admin: 'bg-yellow-700/20 text-yellow-600 dark:bg-yellow-500/20 dark: text-yellow-400',
    member: 'bg-green-500/20 text-green-400',
    viewer: 'bg-gray-500/20 text-gray-400',
  };
  return map[role?.toLowerCase()] ?? 'bg-gray-500/20 text-gray-400';
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A';

const fetchOrganizations = async () => {
  loading.value = true;
  try {
    const data = await getOrganizations();
    organizations.value = data?.organizations ?? data ?? [];
    const active = organizations.value.find(o => o.is_active);
    if (active) activeOrgId.value = active.id;
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
};

const handleCreateOrganization = async () => {
  if (!newOrgName.value.trim()) return;
  try {
    await createOrganization({ name: newOrgName.value.trim(), slug: newOrgSlug.value.trim() });
    await fetchOrganizations();
    closeCreateModal();
    showToast('Organization created successfully!');
  } catch (e) { console.error(e); }
};

const closeCreateModal = () => {
  showCreateModal.value = false;
  newOrgName.value = '';
  newOrgSlug.value = '';
};

const handleSwitchOrg = async (org) => {
  const data = { organization_id: org.id }
  try {
    await switchOrganization(data);
    activeOrgId.value = org.id;
    showToast(`Switched to ${org.name}`);
  } catch (e) { console.error(e); }
};

const manageOrg = async (org) => {
  managingOrg.value = org;
  manageTab.value = 'members';
  editOrgName.value = org.name;
  editOrgSlug.value = org.slug ?? '';
  showInviteRow.value = false;
  showAssignRow.value = false;
  inviteEmail.value = '';
  pendingDevice.value = false;
  inviteRole.value = 'member';
  showManageModal.value = true;
  await fetchOrgMembers(org.id);
  await fetchAllDevices();
  await fetchRelatedDevices(org.id);
};

const closeManageModal = () => {
  showManageModal.value = false;
  managingOrg.value = null;
  orgMembers.value = [];
};

const fetchOrgMembers = async (id) => {
  membersLoading.value = true;
  try {
    const data = await getOrganizationMembers(id);
    const users = await fetchAllUsers();
    orgMembers.value = data?.['members'] ?? data ?? [];
  } catch (e) { console.error(e); }
  finally { membersLoading.value = false; }
};


const fetchAllUsers = async (id) => {
  try {
    const data = await getUsers();
    allUsers.value = data.map((item) => item.email);
  } catch (e) { console.error(e); }
};


const handleInviteMember = async () => {
  if (!inviteEmail.value.trim() || !managingOrg.value) return;
  try {
    await inviteMember(managingOrg.value.id, { email: inviteEmail.value.trim(), role: inviteRole.value });
    await fetchOrgMembers(managingOrg.value.id);
    inviteEmail.value = '';
    showInviteRow.value = false;
    showToast('Member invited successfully!');
  } catch (e) { console.error(e); }
};


const handleAssignDevice = async () => {
  if (!pendingDevice.value || !managingOrg.value) return;
  try {
    await assignDevicetoOrg(managingOrg.value.id, pendingDevice.value);
    pendingDevice.value = '';
    showAssignRow.value = false;
    await fetchRelatedDevices(managingOrg.value.id)
  } catch (error) {
    console.error(error)
  }
};


const handleRemoveMember = async (member) => {
  if (!managingOrg.value) return;
  try {
    await removeMember(managingOrg.value.id, member.user_id);
    await fetchOrgMembers(managingOrg.value.id);
    showToast('Member removed.');
  } catch (e) { console.error(e); }
};


const fetchRelatedDevices = async (orgID) => {
  try {
    const response = await getAllDevicesbyOrg(orgID);
    assignedDevices.value = response;
  } catch (error) {
    console.error(error)
  }
};


const fetchAllDevices = async () => {
  try {
    const response = await getAllDevices();
    allDevices.value = response;
  } catch (error) {
    console.error(error)
  }
};


const deleteOrg = async () => {
  try {
    await deleteOrganization(managingOrg.value.id);
    closeManageModal()
    showToast('Organization deleted successfully!');
  } catch (error) {
    console.error(error);
  }
}

const handleUpdateOrg = async () => {
  if (!editOrgName.value.trim() || !managingOrg.value) return;
  try {
    await updateOrganization(managingOrg.value.id, { name: editOrgName.value.trim(), slug: editOrgSlug.value.trim() });
    await fetchOrganizations();
    showToast('Organization updated successfully!');
  } catch (e) { console.error(e); }
};

const showToast = (msg) => {
  successMessage.value = msg;
  showSuccessToast.value = true;
  setTimeout(() => { showSuccessToast.value = false; }, 3000);
};

onMounted(fetchOrganizations);
</script>